<div class="datetime-picker-modal">
    <Modal
        id={dateTimePickerModal}
        bodyHtml={body}
        titleText={i18n.addDate}
        {dateTimeSet}
        enabled={enableDateTimePickerModal}
        on:close={close}
    >
        <div slot="body">
            <div class={dateTimePickerModalFromSection}>
                <span>
                    <DatePickerField
                        bind:expanded={datePickerFromExpanded}
                        bind:value={dateValueFrom}
                        bind:disabled={isDateDisabled}
                        bind:highlightedDates
                        firstDayOfWeek={configuration.firstDayOfWeek}
                        locale={config.locale}
                        id={datePickerFrom}
                        label={i18n.datePickerLabelStart}
                        labelPosition="left"
                        placeholder={i18n.datePickerPlaceholder}
                        format={dateFormat}
                        closeOnSelect={true}
                        {i18n}
                    />
                </span>
                {#if dateTimeSet && showTimeSelector}
                    <TimeSelector
                        hourLabel={i18n.hour}
                        minuteLabel={i18n.minute}
                        periodLabel=" "
                        config={configuration}
                        bind:hours={hoursFrom}
                        bind:minutes={minutesFrom}
                        bind:isPM={isPMFrom}
                        bind:dateTimeSet
                        bind:isDisabled={isTimeSpanDisabled}
                    />
                {:else}
                    <Button
                        type="plain"
                        htmlType="button"
                        text={i18n.setTime}
                        disabled={connectingEvents.length > 0}
                        on:click={toggleTimeSpanSetting}
                    />
                {/if}
            </div>

            <div class={dateTimePickerModalToSection}>
                <span>
                    <DatePickerField
                        bind:expanded={datePickerToExpanded}
                        bind:value={dateValueTo}
                        bind:disabled={isDateDisabled}
                        bind:highlightedDates
                        firstDayOfWeek={configuration.firstDayOfWeek}
                        locale={config.locale}
                        id={datePickerTo}
                        label={i18n.datePickerLabelStop}
                        labelPosition="left"
                        placeholder={i18n.datePickerPlaceholder}
                        format={configuration.currentDateFormat}
                        closeOnSelect={true}
                        {i18n}
                    />
                    {#if dateTimeSet && showTimeSelector}
                        <TimeSelector
                            hourLabel={i18n.hour}
                            minuteLabel={i18n.minute}
                            periodLabel=" "
                            config={configuration}
                            bind:hours={hoursTo}
                            bind:minutes={minutesTo}
                            bind:isPM={isPMTo}
                            bind:dateTimeSet
                            bind:isDisabled={isTimeSpanDisabled}
                        />
                    {:else}
                        <Button
                            type="plain"
                            htmlType="button"
                            text={i18n.setTime}
                            disabled={connectingEvents.length > 0}
                            on:click={toggleTimeSpanSetting}
                        />
                    {/if}
                </span>
            </div>
            {#if configuration.hasFunctionalityCalendar}
                <EventSelector
                    {i18n}
                    multiplePlans={false}
                    bind:availableEvents
                    bind:allEvents
                    {planIds}
                />
            {/if}

            <div
                role="alert"
                aria-live="assertive"
                class="datetime-picker-modal__error"
                hidden={showStartStopErrorMessage ||
                showStartInvalidMessage ||
                showStopInvalidMessage
                    ? ""
                    : "hidden"}
            >
                {showStartStopErrorMessage
                    ? i18n.errorStopDateLessThanStartDate
                    : showStartInvalidMessage
                      ? i18n.calendar_Recurring_invalid_from_date
                      : i18n.calendar_Recurring_invalid_to_date}
            </div>
        </div>

        <div slot="footer">
            <Button type="primary" large={true} text={i18n.save} on:click={confirm} />
            <Button type="destructive" large={true} text={i18n.clear} on:click={clear} />
            <Button type="secondary" large={true} text={i18n.close} on:click={close} />
        </div>
    </Modal>
</div>

<script>
    import Button from "@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte";
    import Modal from "@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte";
    import DatePickerField from "@itslearning/prometheus/assets/inputs/DatePickerField/v1/DatePickerField.svelte";
    import { createEventDispatcher, onMount } from "svelte";
    import config from "../../config";
    import i18n from "../../i18n.js";
    import TimeSelector from "./TimeSelector.svelte";
    import EventSelector from "./EventSelector.svelte";
    import {
        isDateOnlyMode,
        getEventsDateRangeString,
        formatDateOnlyForApi,
        validateDateFormat,
        initAutoSuggestFromDate,
        initAutoSuggestToDate
    } from "../../helpers/dateHelper";
    import {
        defaultFromHour,
        defaultToHour,
        dateTimePickerModalFromSection,
        dateTimePickerModalToSection,
        fromDatePickerInputField,
        toDatePickerInputField
    } from "../../constants";
    import { getCalendarAvailableDates } from "../../api";

    export let enableDateTimePickerModal;
    export let dateTimePickerModal;
    export let datePickerFrom;
    export let datePickerTo;
    export let datePickerFromExpanded = false;
    export let datePickerToExpanded = false;
    export let planResources;

    export let body;

    export let dateValueFrom = "";
    export let hoursFrom;
    export let minutesFrom;
    export let isPMFrom;

    export let dateValueTo = "";
    export let hoursTo;
    export let minutesTo;
    export let isPMTo;

    export let dateTimeSet;
    export let planIds;

    let showStartStopErrorMessage = false;
    let showStartInvalidMessage = false;
    let showStopInvalidMessage = false;

    let showTimeSelector = false;
    let isTimeSpanDisabled = true;
    let isDateDisabled = false;
    let dateValueFromInitially = null;
    let dateValueToInitially = null;

    const dateFormat = config.currentDateFormat;

    let connectingEvents = [];
    let allEvents = [];
    let availableEvents = [];
    let highlightedDates = [];

    const configuration = planResources ? planResources : config;

    let mountComplete = false;

    $: isTimeSpanDisabled = !showTimeSelector;

    $: if (
        mountComplete &&
        dateValueFrom &&
        dateValueFromInitially !== dateValueFrom &&
        !datePickerFromExpanded
    ) {
        checkAutoSuggestToDate();
        getCourseEventsInCurrentTimespan(false);
    }

    $: if (
        mountComplete &&
        dateValueTo &&
        dateValueToInitially !== dateValueTo &&
        !datePickerToExpanded
    ) {
        checkAutoSuggestFromDate();
        getCourseEventsInCurrentTimespan(false);
    }

    $: if (
        mountComplete &&
        showTimeSelector &&
        (hoursFrom || minutesFrom || isPMFrom || hoursTo || minutesTo || isPMTo)
    ) {
        getCourseEventsInCurrentTimespan(false);
    }

    $: if (availableEvents) {
        connectingEvents = availableEvents.filter(
            p => p.eventConnected === true && p.eventOccupied === false
        );

        if (connectingEvents.length > 0) {
            isDateDisabled = true;
            isTimeSpanDisabled = true;
        } else {
            isDateDisabled = false;
            isTimeSpanDisabled = !showTimeSelector;
        }
    }

    onMount(() => {
        const focusElement = "#prom-input-" + datePickerFrom + "-input";

        if (document.querySelector(focusElement)) {
            document.querySelector(focusElement).focus();
        }

        if (dateTimeSet === true && !isDateOnlyMode(parseInt(minutesFrom), parseInt(minutesTo))) {
            showTimeSelector = true;
            isTimeSpanDisabled = false;
        } else {
            showTimeSelector = false;
            isTimeSpanDisabled = true;
        }

        dateValueFromInitially = dateValueFrom;
        initAutoSuggestFromDate(dateValueFrom, dateValueTo, dateValueFromInitially);

        dateValueToInitially = dateValueTo;
        initAutoSuggestToDate(dateValueFrom, dateValueTo, dateValueToInitially);

        getAllCourseEvents();

        mountComplete = true;
    });

    function checkAutoSuggestFromDate() {
        if (dateValueTo && !dateValueFromInitially) {
            dateValueFrom = new Date(dateValueTo);
            dateValueToInitially = dateValueTo;
            dateValueFromInitially = dateValueFrom;
        }
    }

    function checkAutoSuggestToDate() {
        const fromDateElement = document.getElementById(fromDatePickerInputField);
        const dateFrom = fromDateElement ? fromDateElement.value : "";

        if (!validateDateFormat(dateFrom, configuration.currentDateFormat)) {
            return;
        }

        if (dateValueFrom && !dateValueToInitially) {
            dateValueTo = new Date(dateValueFrom);
            dateValueToInitially = dateValueTo;
            dateValueFromInitially = dateValueFrom;
        }
    }

    const dispatch = createEventDispatcher();

    // Function to calculate and save the plan date and also notify the single plan page about it for closing and updating
    // An error message will occur if you attempt to set start date after stop date
    function confirm() {
        const datePickerFrom = dateValueFrom ? new Date(dateValueFrom) : undefined;
        const datePickerTo = dateValueTo ? new Date(dateValueTo) : undefined;

        const dateFrom = document.getElementById(fromDatePickerInputField).value;
        const dateTo = document.getElementById(toDatePickerInputField).value;

        if (!dateFrom && !dateTo) {
            dispatch("confirm", {
                deleteDates: true
            });
        }

        const validateDateToFormat = validateDateFormat(dateTo, dateFormat);
        const validateDateFromFormat = validateDateFormat(dateFrom, dateFormat);

        if (!datePickerFrom && !datePickerTo && validateDateFromFormat && validateDateToFormat) {
            close();

            return;
        }

        if (!datePickerFrom || !datePickerTo || !validateDateFromFormat || !validateDateToFormat) {
            if (!datePickerFrom || !validateDateFromFormat) {
                showStartInvalidMessage = true;
                showStopInvalidMessage = false;
                showStartStopErrorMessage = false;

                return;
            } else if (!datePickerTo || !validateDateToFormat) {
                showStopInvalidMessage = true;
                showStartInvalidMessage = false;
                showStartStopErrorMessage = false;

                return;
            }
        }

        insertHoursAndMinutes(datePickerFrom, datePickerTo);

        // Use epoch time to compare time (ms since 1970)
        const epochStart = datePickerFrom.valueOf();
        const epochStop = datePickerTo.valueOf();

        if (epochStart > epochStop) {
            showStartStopErrorMessage = true;

            return;
        } else {
            showStartStopErrorMessage = false;
        }

        if (connectingEvents.length > 0) {
            const activityArray = [];

            connectingEvents.forEach(event => {
                if (event.eventId > 0) {
                    activityArray.push(event.eventId.toString());
                }
            });

            dispatch("confirm", {
                newStartDate: undefined,
                newStopDate: undefined,
                connectedToEvent: true,
                activityIds: activityArray
            });
        } else {
            dispatch("confirm", {
                newStartDate: datePickerFrom,
                newStopDate: datePickerTo,
                connectedToEvent: false,
                activityIds: undefined
            });
        }
    }

    function insertHoursAndMinutes(datePickerFrom, datePickerTo) {
        if (showTimeSelector) {
            let hoursFromVal = parseInt(hoursFrom);

            if (configuration.useTwelveHourFormat) {
                if (hoursFromVal === 12) {
                    hoursFromVal = 0;
                }

                if (isPMFrom) {
                    hoursFromVal = hoursFromVal + 12;
                }
            }

            datePickerFrom.setHours(hoursFromVal, parseInt(minutesFrom));

            let hoursToVal = parseInt(hoursTo);

            if (configuration.useTwelveHourFormat) {
                if (hoursToVal === 12) {
                    hoursToVal = 0;
                }

                if (isPMTo) {
                    hoursToVal = hoursToVal + 12;
                }
            }

            datePickerTo.setHours(hoursToVal, parseInt(minutesTo));
        } else {
            datePickerFrom.setHours(0, 1);
            datePickerTo.setHours(23, 59);
        }
    }

    function clear() {
        dateValueFrom = null;
        dateValueTo = null;
        dateValueFromInitially = null;
        dateValueToInitially = null;
        showTimeSelector = false;
        availableEvents = [];
        const selectAllHeaderCheckboxElement = document.querySelector(
            ".connect-event-section .prom-list-element--header .prom-checkbox__input"
        );

        if (selectAllHeaderCheckboxElement) {
            selectAllHeaderCheckboxElement.checked = false;
        }

        dispatch("clear");
    }

    function close() {
        dispatch("cancel");
    }

    function toggleTimeSpanSetting() {
        if (showTimeSelector === true) {
            showTimeSelector = false;
        } else {
            if (isDateOnlyMode(parseInt(minutesFrom), parseInt(minutesTo))) {
                hoursFrom = defaultFromHour;
                minutesFrom = "00";
                hoursTo = defaultToHour;
                minutesTo = "00";
            }

            showTimeSelector = true;
        }

        dateTimeSet = showTimeSelector;
    }

    // Function to get course events in the currently selected timespan
    // As long as selected start or stop time is within event range it should also appear
    function getCourseEventsInCurrentTimespan(setConnectingEvents) {
        const datePickerFrom = dateValueFrom ? new Date(dateValueFrom) : undefined;
        const datePickerTo = dateValueTo ? new Date(dateValueTo) : undefined;

        if (!datePickerFrom || !datePickerTo) {
            return;
        }

        insertHoursAndMinutes(datePickerFrom, datePickerTo);

        const updatedAvailableEvents = [];

        allEvents.forEach(function (event) {
            if (
                (datePickerFrom >= event.eventFrom && datePickerFrom <= event.eventTo) ||
                (datePickerTo >= event.eventFrom && datePickerTo <= event.eventTo) ||
                (event.eventFrom >= datePickerFrom && event.eventTo <= datePickerTo)
            ) {
                updatedAvailableEvents.push({
                    eventId: event.eventId,
                    eventOccupied: event.eventOccupied,
                    eventConnected: setConnectingEvents ? event.eventConnected : false,
                    eventDate: event.eventDate,
                    eventTitle: event.eventTitle,
                    groupId: event.groupId,
                    participants: event.participants,
                    eventFrom: event.eventFrom,
                    eventTo: event.eventTo
                });
            }
        });
        availableEvents = updatedAvailableEvents;
    }

    // Function to get all available course events
    function getAllCourseEvents() {
        if (!config.hasFunctionalityCalendar || config.childId > 0) {
            return;
        }

        const datePickerMin = new Date(1999, 1, 1);
        const datePickerMax = new Date(2999, 12, 31);

        formatDateOnlyForApi(datePickerMin, datePickerMax);

        getCalendarAvailableDates(datePickerMin, datePickerMax, config.filterPersonId).then(events => {
            const pickerHighlightedDates = [];
            const allCourseEvents = [];

            let multipleDaysPlan = false;
            let firstEventStartDate = undefined;
            let firstEventStopDate = undefined;

            if (events.length > 0) {
                firstEventStartDate = new Date(events[0].Start);
                firstEventStopDate = new Date(events[0].Stop);

                if (firstEventStartDate != firstEventStopDate) {
                    multipleDaysPlan = true;
                }
            }

            // Check if any of the dates differs so we need to include date (in addition to time)
            if (firstEventStartDate && !multipleDaysPlan) {
                let curentEventStartDate = undefined;
                let currentEventStopDate = undefined;

                events.forEach(function (event) {
                    curentEventStartDate = new Date(event.Start);
                    currentEventStopDate = new Date(event.Stop);

                    if (
                        curentEventStartDate !== firstEventStartDate ||
                        currentEventStopDate !== firstEventStartDate
                    ) {
                        multipleDaysPlan = true;
                    }
                });
            }

            events.forEach(function (event) {
                const eventFrom = event.Start ? new Date(event.Start) : undefined;
                const eventTo = event.Stop ? new Date(event.Stop) : undefined;

                if (eventFrom && eventTo) {
                    const formattedDate = getEventsDateRangeString(
                        eventFrom,
                        eventTo,
                        config.locale,
                        multipleDaysPlan
                    );

                    let isOccupied = false;
                    let isConnected = false;

                    if (event.PlanId === configuration.planId) {
                        isConnected = true;
                    } else {
                        if (event.PlanId && event.PlanId !== 0 && !event.PlanDeleted) {
                            isOccupied = true;
                        }
                    }

                    allCourseEvents.push({
                        eventId: event.EventId,
                        eventOccupied: isOccupied,
                        eventConnected: isConnected,
                        eventDate: formattedDate,
                        eventTitle: event.EventTitle,
                        participants: event.GroupTitle,
                        eventFrom: eventFrom,
                        eventTo: eventTo
                    });

                    if (
                        event &&
                        (event.PlanId === 0 ||
                            event.PlanId === configuration.planId ||
                            event.PlanDeleted)
                    ) {
                        const epochStart = eventFrom.valueOf();
                        const epochStop = eventTo.valueOf();

                        const dayCounter = Math.floor(
                            (epochStop - epochStart) / (1000 * 60 * 60 * 24)
                        );

                        if (dayCounter > 0) {
                            for (let i = 0; i <= dayCounter; i++) {
                                const currentDatePointer = new Date();

                                currentDatePointer.setTime(epochStart + i * (1000 * 60 * 60 * 24));
                                pickerHighlightedDates.push(currentDatePointer);
                            }
                        } else {
                            pickerHighlightedDates.push(eventFrom);
                        }
                    }
                }
            });
            highlightedDates = pickerHighlightedDates;
            allEvents = allCourseEvents;
            getCourseEventsInCurrentTimespan(true);
        });
    }
</script>
